import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
	useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { requestNameMyDrink } from '../../../../services/Clint/NamedDrinkRequestClintApi';

const RenameDrinkDialog = ({ open, onClose, drink, drinkId, onSuccess }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const [drinkName, setDrinkName] = useState('');
	const [notes, setNotes] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [successMessage, setSuccessMessage] = useState('');

	const targetDrinkId = useMemo(() => {
		return drinkId || drink?.id || drink?.drink_id || null;
	}, [drinkId, drink]);

	useEffect(() => {
		if (open) {
			setDrinkName('');
			setNotes('');
			setError('');
			setSuccessMessage('');
		}
	}, [open]);

	const handleSubmit = async () => {
		const cleanName = drinkName.trim();

		if (!cleanName) {
			setError('من فضلك أدخل الاسم المقترح للمشروب');
			return;
		}

		if (!targetDrinkId) {
			setError('لم يتم تحديد المشروب المراد تسميته');
			return;
		}

		setLoading(true);
		setError('');
		setSuccessMessage('');

		try {
			const response = await requestNameMyDrink({
				drink_name: cleanName,
				drink_name_en: cleanName,
				notes: notes.trim(),
				drink_id: targetDrinkId,
			});

			setSuccessMessage(response?.message || 'تم إرسال طلب تسمية المشروب بنجاح');

			if (onSuccess) {
				onSuccess(response?.data);
			}

			setTimeout(() => {
				onClose?.();
			}, 900);
		} catch (err) {
			console.error('خطأ أثناء إرسال طلب تسمية المشروب:', err);
			setError(err.message || 'حدث خطأ أثناء إرسال الطلب');
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm" fullScreen={isMobile} dir="rtl">
			<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
				<EditNoteIcon color="primary" />
				تسمية المشروب
			</DialogTitle>

			<DialogContent dividers sx={{ p: { xs: 1.5, sm: 2 } }}>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
					{drink && (
						<Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: 'grey.100' }}>
							<Typography variant="body2" color="text.secondary">
								المشروب المحدد: {drink.drink_name || drink.name || `#${targetDrinkId}`}
							</Typography>
						</Box>
					)}

					{error && <Alert severity="error">{error}</Alert>}
					{successMessage && <Alert severity="success">{successMessage}</Alert>}

					<TextField
						label="الاسم المقترح"
						value={drinkName}
						onChange={(e) => setDrinkName(e.target.value)}
						size={isMobile ? 'small' : 'medium'}
						fullWidth
						required
						disabled={loading}
					/>

					<TextField
						label="ملاحظات"
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
						fullWidth
						multiline
						minRows={3}
						size={isMobile ? 'small' : 'medium'}
						disabled={loading}
					/>
				</Box>
			</DialogContent>

			<DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1 }}>
				<Button onClick={onClose} disabled={loading}>
					إلغاء
				</Button>
				<Button
					variant="contained"
					onClick={handleSubmit}
					disabled={loading}
					startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
					fullWidth={isMobile}
				>
					إرسال الطلب
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default RenameDrinkDialog;
